import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SHEETDB_API = 'https://sheetdb.io/api/v1/d9kvrynuj3y7h';

interface ShipmentRow {
  [key: string]: string;
}

interface TrackingResult {
  trackingNumber: string;
  status: string;
  currentLocation: string;
  estimatedDelivery: string;
  progress: number;
  origin: string;
  destination: string;
  driver: string;
  notes: string;
  updates: { date: string; location: string; status: string }[];
  raw: ShipmentRow;
}

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[\s_\-]+/g, '_');
}

function findField(row: ShipmentRow, candidates: string[]): string {
  const keys = Object.keys(row);
  for (const candidate of candidates) {
    const match = keys.find(
      (k) => normalizeKey(k) === normalizeKey(candidate)
    );
    if (match && row[match]) return row[match];
  }
  return '';
}

function parseProgress(val: string): number {
  if (!val) return 0;
  const n = parseInt(val.replace('%', ''), 10);
  return isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
}

function mapRow(row: ShipmentRow): TrackingResult {
  const trackingNumber = findField(row, [
    'tracking_number', 'tracking', 'id', 'order_id', 'shipment_id', 'number',
  ]);
  const status = findField(row, ['status', 'shipment_status', 'state']);
  const currentLocation = findField(row, [
    'current_location', 'location', 'current_city', 'city', 'position',
  ]);
  const estimatedDelivery = findField(row, [
    'estimated_delivery', 'eta', 'delivery_date', 'expected_delivery',
    'estimated_arrival', 'arrival_date',
  ]);
  const progressRaw = findField(row, ['progress', 'completion', 'percent', 'percentage']);
  const origin = findField(row, ['origin', 'from', 'pickup', 'pickup_location', 'from_city']);
  const destination = findField(row, [
    'destination', 'to', 'delivery', 'delivery_location', 'to_city',
  ]);
  const driver = findField(row, ['driver', 'driver_name', 'assigned_driver', 'carrier']);
  const notes = findField(row, ['notes', 'remarks', 'comments', 'note', 'description']);

  // Build timeline updates from dedicated columns if present, or synthesize from available data
  const updates: { date: string; location: string; status: string }[] = [];

  // Try update_1, update_2, etc.
  for (let i = 1; i <= 10; i++) {
    const upStatus = findField(row, [`update_${i}_status`, `status_${i}`, `event_${i}`]);
    const upLocation = findField(row, [
      `update_${i}_location`, `location_${i}`, `city_${i}`,
    ]);
    const upDate = findField(row, [`update_${i}_date`, `date_${i}`, `time_${i}`]);
    if (upStatus || upDate) {
      updates.push({
        status: upStatus || status,
        location: upLocation || currentLocation,
        date: upDate || '',
      });
    }
  }

  // If no dedicated update columns found, synthesize from available fields
  if (updates.length === 0) {
    if (origin) {
      updates.push({
        status: 'Picked up',
        location: origin,
        date: findField(row, ['pickup_date', 'start_date', 'date', 'created_at']) || '',
      });
    }
    if (currentLocation && currentLocation !== origin) {
      updates.push({
        status: status || 'In Transit',
        location: currentLocation,
        date: findField(row, ['updated_at', 'last_updated', 'update_date']) || '',
      });
    }
    if (
      destination &&
      destination !== currentLocation &&
      (status?.toLowerCase().includes('deliver') ||
        status?.toLowerCase().includes('complet') ||
        status?.toLowerCase().includes('arriv'))
    ) {
      updates.push({
        status: 'Delivered',
        location: destination,
        date: estimatedDelivery || '',
      });
    }
  }

  // Auto-compute progress from status if not provided
  let progress = parseProgress(progressRaw);
  if (progress === 0 && status) {
    const s = status.toLowerCase();
    if (s.includes('picked') || s.includes('pickup')) progress = 10;
    else if (s.includes('transit') || s.includes('moving')) progress = 50;
    else if (s.includes('facility') || s.includes('arrived')) progress = 75;
    else if (s.includes('out for delivery') || s.includes('out_for')) progress = 90;
    else if (s.includes('deliver') || s.includes('complet')) progress = 100;
    else progress = 30;
  }

  return {
    trackingNumber: trackingNumber || Object.values(row)[0] || 'N/A',
    status: status || 'Status unavailable',
    currentLocation: currentLocation || 'Location unavailable',
    estimatedDelivery: estimatedDelivery || '',
    progress,
    origin,
    destination,
    driver,
    notes,
    updates,
    raw: row,
  };
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  let color = 'bg-electric-blue/20 border-electric-blue/50 text-white';
  let dot = 'bg-electric-blue';
  if (s.includes('deliver') || s.includes('complet')) {
    color = 'bg-green-500/20 border-green-500/50 text-green-300';
    dot = 'bg-green-400';
  } else if (s.includes('delay') || s.includes('hold') || s.includes('issue')) {
    color = 'bg-red-500/20 border-red-500/50 text-red-300';
    dot = 'bg-red-400';
  } else if (s.includes('transit') || s.includes('moving') || s.includes('picked')) {
    color = 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300';
    dot = 'bg-yellow-400 animate-pulse';
  }
  return (
    <div className={`inline-flex items-center space-x-2 px-4 py-2 border rounded-full ${color}`}>
      <div className={`w-2 h-2 rounded-full ${dot}`} />
      <span className="font-inter font-semibold text-sm">{status}</span>
    </div>
  );
}

export default function ShipmentTracking() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [trackingData, setTrackingData] = useState<TrackingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    setIsTracking(true);
    setError(null);
    setNotFound(false);
    setTrackingData(null);

    try {
      // Try searching by common tracking number column names
      const searchColumns = [
        'tracking_number', 'tracking', 'id', 'order_id', 'shipment_id', 'number',
      ];

      let found: ShipmentRow | null = null;

      // First, search using SheetDB search endpoint for each column
      for (const col of searchColumns) {
        if (found) break;
        try {
          const res = await fetch(
            `${SHEETDB_API}/search?${encodeURIComponent(col)}=${encodeURIComponent(trackingNumber.trim())}&limit=1`
          );
          if (res.ok) {
            const data: ShipmentRow[] = await res.json();
            if (Array.isArray(data) && data.length > 0) {
              found = data[0];
              break;
            }
          }
        } catch {
          // Continue to next column
        }
      }

      // Fallback: fetch all rows and do a client-side search
      if (!found) {
        const res = await fetch(SHEETDB_API);
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        const allRows: ShipmentRow[] = await res.json();

        if (Array.isArray(allRows)) {
          const input = trackingNumber.trim().toLowerCase();
          found =
            allRows.find((row) =>
              Object.values(row).some(
                (val) => typeof val === 'string' && val.toLowerCase() === input
              )
            ) || null;
        }
      }

      if (!found) {
        setNotFound(true);
      } else {
        setTrackingData(mapRow(found));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Could not connect to tracking system. Please try again. (${message})`);
    } finally {
      setIsTracking(false);
    }
  };

  return (
    <section className="py-24 bg-dark-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            TRACK YOUR <span className="text-electric-blue">SHIPMENT</span>
          </h2>
          <p className="font-inter text-white/60 text-lg">
            Real-time updates on your transportation status
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <form onSubmit={handleTrack} className="mb-8">
            <div className="flex gap-4">
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                required
                className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white font-inter text-lg focus:outline-none focus:border-electric-blue focus:shadow-lg focus:shadow-electric-blue/20 transition-all duration-300 placeholder-white/40"
                placeholder="Enter tracking number..."
              />
              <button
                type="submit"
                disabled={isTracking}
                className="px-8 py-4 bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 font-orbitron font-bold text-base rounded-full hover:shadow-lg hover:shadow-electric-blue/50 transition-all duration-300 disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {isTracking ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-xl w-5 h-5 flex items-center justify-center" />
                    SEARCHING...
                  </span>
                ) : (
                  'TRACK'
                )}
              </button>
            </div>
          </form>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center"
              >
                <i className="ri-error-warning-line text-red-400 text-3xl w-8 h-8 flex items-center justify-center mx-auto mb-3" />
                <p className="font-inter text-red-300">{error}</p>
              </motion.div>
            )}

            {notFound && (
              <motion.div
                key="notfound"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center"
              >
                <i className="ri-search-line text-white/30 text-5xl w-12 h-12 flex items-center justify-center mx-auto mb-4" />
                <p className="font-orbitron text-white/60 text-lg mb-2">No Shipment Found</p>
                <p className="font-inter text-white/40 text-sm">
                  No shipment found for tracking number{' '}
                  <span className="text-electric-blue font-semibold">{trackingNumber}</span>.
                  Please check your number and try again.
                </p>
              </motion.div>
            )}

            {trackingData && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 space-y-8"
              >
                {/* Status Header */}
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="font-orbitron text-xs text-electric-blue tracking-wider mb-2">
                      TRACKING NUMBER
                    </div>
                    <div className="font-orbitron text-2xl font-bold text-white">
                      {trackingData.trackingNumber}
                    </div>
                    {trackingData.driver && (
                      <div className="font-inter text-white/50 text-sm mt-1">
                        Driver: <span className="text-white/80">{trackingData.driver}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-orbitron text-xs text-electric-blue tracking-wider mb-2">
                      STATUS
                    </div>
                    <StatusBadge status={trackingData.status} />
                  </div>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-inter text-white/60 text-sm">Delivery Progress</span>
                    <span className="font-inter text-electric-blue font-semibold text-sm">
                      {trackingData.progress}%
                    </span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${trackingData.progress}%` }}
                      transition={{ duration: 1, delay: 0.3 }}
                      className="h-full bg-gradient-to-r from-electric-blue to-electric-cyan rounded-full"
                    />
                  </div>
                  {trackingData.origin && trackingData.destination && (
                    <div className="flex justify-between mt-2">
                      <span className="font-inter text-white/40 text-xs">{trackingData.origin}</span>
                      <span className="font-inter text-white/40 text-xs">{trackingData.destination}</span>
                    </div>
                  )}
                </div>

                {/* Location & ETA Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white/5 rounded-2xl p-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <i className="ri-map-pin-line text-2xl text-electric-blue w-6 h-6 flex items-center justify-center" />
                      <span className="font-orbitron text-xs text-electric-blue tracking-wider">
                        CURRENT LOCATION
                      </span>
                    </div>
                    <div className="font-inter text-white text-lg font-semibold">
                      {trackingData.currentLocation}
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-2xl p-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <i className="ri-calendar-check-line text-2xl text-electric-blue w-6 h-6 flex items-center justify-center" />
                      <span className="font-orbitron text-xs text-electric-blue tracking-wider">
                        ESTIMATED DELIVERY
                      </span>
                    </div>
                    <div className="font-inter text-white text-lg font-semibold">
                      {trackingData.estimatedDelivery
                        ? (() => {
                            const d = new Date(trackingData.estimatedDelivery);
                            return isNaN(d.getTime())
                              ? trackingData.estimatedDelivery
                              : d.toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric',
                                });
                          })()
                        : 'To be confirmed'}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {trackingData.notes && (
                  <div className="bg-electric-blue/5 border border-electric-blue/20 rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-information-line text-electric-blue text-lg w-5 h-5 flex items-center justify-center" />
                      <span className="font-orbitron text-xs text-electric-blue tracking-wider">
                        NOTES
                      </span>
                    </div>
                    <p className="font-inter text-white/70 text-sm">{trackingData.notes}</p>
                  </div>
                )}

                {/* Timeline */}
                {trackingData.updates.length > 0 && (
                  <div>
                    <h3 className="font-orbitron text-xs text-electric-blue tracking-wider mb-6">
                      SHIPMENT HISTORY
                    </h3>
                    <div className="space-y-4">
                      {trackingData.updates.map((update, index) => (
                        <div key={index} className="flex items-start space-x-4">
                          <div className="relative flex-shrink-0">
                            <div className="w-4 h-4 rounded-full bg-electric-blue border-4 border-dark-900" />
                            {index < trackingData.updates.length - 1 && (
                              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-0.5 h-12 bg-electric-blue/30" />
                            )}
                          </div>
                          <div className="flex-1 pb-8">
                            <div className="font-inter text-white font-semibold text-sm mb-1">
                              {update.status}
                            </div>
                            {update.location && (
                              <div className="font-inter text-white/60 text-xs mb-1">
                                {update.location}
                              </div>
                            )}
                            {update.date && (
                              <div className="font-inter text-white/40 text-xs">{update.date}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
